import {
  Checkbox,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, styled } from '@mui/system';
import moment from 'moment';
import React, { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SbButton from '../../components/Button';
import ExportBtn from '../../components/Button/Export';
import DateRangePicker from '../../components/DateRangePicker';
import FilterSelect from '../../components/Filter/Dropdown';
import FilterSearch from '../../components/Filter/Search';
import { SBTable, SBTableWrapper } from '../../components/Table/Table';
import { TableHeadCell, TableRowCell } from '../../components/Table/TableCell';
import { SBTableRow } from '../../components/Table/TableRow';
import Title from '../../components/Title';
import Navigation from './Navigation';

// ICONS
import ClearIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CspLabelIcon from '@mui/icons-material/HandshakeOutlined';
import ShippingLabelIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import ShipmentInvoiceIcon from '@mui/icons-material/SaveOutlined';
import ProformaInvoiceIcon from '@mui/icons-material/SummarizeOutlined';
import { resetManifest, resetManifestSuccess } from '@Reducers/Manifest';
import DownloadShipmentLabelAndInvoice from '@Components/DownloadShipmentLabelAndInvoice';
import Loader from '@Components/Loader';
import ShipmentKycUpload from '@Components/ShipmentKycUpload';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { getLoggedInUserSystemList } from '@Reducers/Systems';
import { useNavigate } from 'react-router-dom';
import { DropdownOptionType } from '../../components/Dropdown/Type';
import Image from '../../components/Image';
import { ArrowDropDownCircleOutlined } from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AISmartSearch from './AISmartSearch';
import { AISearchResult } from './AISmartSearch/types';
import API from '@Services/apiAxios';
import {
  CSP_TYPES,
  hideDownloadCarrierAWBAction,
  SHIPMENT_ROUTE_TYPE,
  SHIPMENT_STATUS,
} from '../../constants/Shipment';
import { hasAdminAccess, userLogin } from '../../services/hasAdminAccess';
import { AppDispatch, RootStateType } from '../../store';
import { UserType } from '../../store/reducers/Shipment/Type';
import { getShipmentList, setSelectedShipments as setSelectedShips } from '../../store/reducers/ShipmentHistory';
import {
  downloadBulkProformaInvoice,
  downloadBulkShippingInvoice,
  downloadBulkShippingLabel,
  downloadCarrierLabel,
} from '../../store/reducers/ShipmentHistory/actions';
import { ShipmentHistoryQuery, ShipmentObj } from '../../store/reducers/ShipmentHistory/Type';
import { isActionAllowed } from '../../utils/allowedActions';
import CreateManifestSuccessModal from '../Manifest/SuccessModal';
import BulkStatusUpload from './BulkStatusUpload';
import CreateManifest from './CreateManifest';
import DownloadOptions from './DonloadOptions';
import { FileDownloadOptionPropsType, FilterObj, getFilteredServiceList } from './Types';
import UpdateStatus from './UpdateStatus';
import { fetchShipmentReports } from '@Reducers/shipmentReportsDownloadSlice/shipmentReportsDownloadSlice';
import { SuccessErrorModalOpen, SuccessErrorModalReset } from '@Reducers/SuccessErrorModal';
import { toggleBoolean } from '@Reducers/booleanSlice/booleanSlice';
import { getAllReadNotifications, getAllUnreadNotifications } from '@Reducers/Notifications/actions';
import PhoneIphoneSharpIcon from '@mui/icons-material/PhoneIphoneSharp';
import LaptopSharpIcon from '@mui/icons-material/LaptopSharp';
import AccountTreeSharpIcon from '@mui/icons-material/AccountTreeSharp';
import Tooltip from '@mui/material/Tooltip';
import BulkAWBUpload from './BulkAWBUpload/BulkAWBUpload';
import { getCarrierList } from '@Reducers/Carrier';
import { getShipmentInvoiceReport } from '@Reducers/shipmentReportsDownloadSlice/shipmentInvoiceReportSlice';

// ============================================================================
// CONSTANTS - Moved outside to prevent recreation
// ============================================================================
const SearchFilterList = [
  { label: 'Name', value: 'name' },
  { label: 'Email', value: 'email' },
  { label: 'Account no.', value: 'accountNumber' },
  { label: 'Company Name', value: 'companyName' },
  { label: 'Shipment Number', value: 'shipmentNumber' },
  { label: 'AWB Number', value: 'awb' },
  { label: 'Invoice Number', value: 'invoiceNumber' },
  { label: 'Batch Number', value: 'batchNumber' },
  { label: 'Reference Number', value: 'referenceNumber' },
];

const AdminSearchFilterList = [
  { label: 'Name', value: 'name' },
  { label: 'Email', value: 'email' },
  { label: 'Account no.', value: 'accountNumber' },
  { label: 'Company Name', value: 'companyName' },
  { label: 'Manifest Number', value: 'manifest_number' },
  { label: 'Shipment Number', value: 'shipmentNumber' },
  { label: 'AWB Number', value: 'awb' },
  { label: 'Invoice Number', value: 'invoiceNumber' },
  { label: 'Reference Number', value: 'referenceNumber' },
  { label: 'Batch Number', value: 'batchNumber' },
];

const StatusList = Object.values(SHIPMENT_STATUS)
  .filter(item => item.value !== 'unprocessed' && item.value !== 'draft')
  .map(s => ({ display: s.label, value: s.value }));

const ALLOWED_CARRIER_CODES = [
  CSP_TYPES.fedex.value,
  CSP_TYPES.linex.value,
  CSP_TYPES.ithinklogistics.value,
  CSP_TYPES.ithink_logistics_domestic.value,
  CSP_TYPES.delhivery.value,
  CSP_TYPES.trackon.value,
  CSP_TYPES.ups.value,
  CSP_TYPES.emirates_skycargo.value,
  CSP_TYPES.dpduk.value,
  CSP_TYPES.dhlIndia.value,
  CSP_TYPES.dhl.value,
  CSP_TYPES.stage.value,
  CSP_TYPES.universal.value,
  'cirro',
  CSP_TYPES.aramex.value,
];

const TypeList: Array<DropdownOptionType> = [
  { display: 'HYPERLOCAL', value: 'hyperlocal' },
  { display: 'DOMESTIC', value: 'domestic' },
  { display: 'IMPORT', value: 'import' },
  { display: 'EXPORT', value: 'export' },
];

const PlatformList: Array<DropdownOptionType> = [
  { display: 'Web', value: 'web' },
  { display: 'Mobile', value: 'mobile' },
  { display: 'API', value: 'inter organization' },
];

const InvoicedList: Array<DropdownOptionType> = [
  { display: 'Paid', value: 'paid' },
  { display: 'Unpaid', value: 'unpaid' },
  { display: 'Billed', value: 'billed' },
  { display: 'Unbilled', value: 'unbilled' },
];

const bulkUploadOptions: { value: string; label: string }[] = [
  { value: 'status', label: 'Status' },
  { value: 'AWB', label: 'AWB' },
];

const fileDownloadDefaultOption: FileDownloadOptionPropsType = {
  isOpen: false,
  title: '',
  downloadOption: 'fileFormat',
  handleDownload: () => {
    console.log('handle File Download');
  },
};

const ButtonSx = {
  display: 'flex',
  justifyContent: 'space-between',
  borderRadius: '8px !important',
  backgroundColor: 'var(--mui-palette-primary-light)',
  whiteSpace: 'nowrap',
};

// ============================================================================
// UTILITY FUNCTIONS - Moved outside
// ============================================================================
function hasIsTransit(statusHistory?: string[]): boolean {
  if (!Array.isArray(statusHistory)) return false;
  return statusHistory.includes('in_transit');
}

function getCarrierImage(shipment: ShipmentObj, isAdmin: boolean): string {
  if (!shipment?.vendorShipmentModel) return '';

  if (isAdmin) {
    const code = shipment?.buyingPricing?.code;

    const isAllowedCarrier = typeof code === 'string' && ALLOWED_CARRIER_CODES.includes(code);

    return isAllowedCarrier ? shipment?.buyingPricing?.serviceImage ?? '' : '';
  }

  const buyingImage = shipment?.buyingPricing?.serviceImage ?? '';
  const sellingImage = shipment?.sellingPricing?.serviceImage ?? '';

  return buyingImage === sellingImage ? sellingImage : '';
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================
const TableActionContainer = styled('div')<{ isAdmin?: '1' | '0' }>(({ theme, isAdmin = false }) => ({
  padding: '32px 0px',
  display: 'grid',
  gridTemplateColumns:
    isAdmin === '1'
      ? '510px 1fr fit-content(140px) fit-content(140px) 150px 150px 150px 140px 140px 150px 140px'
      : '510px 1fr fit-content(140px) fit-content(140px) 150px 140px',
  columnGap: '15px',
  [theme.breakpoints.down('xl')]: {
    padding: '32px 0px',
    columnGap: 0,
    gridTemplateColumns:
      isAdmin === '1'
        ? '490px 1fr fit-content(140px) fit-content(140px) 150px 150px 150px 140px 140px 150px 140px'
        : '490px 1fr fit-content(140px) fit-content(140px) 150px 140px',
  },
}));

// ============================================================================
// MEMOIZED SUB-COMPONENTS
// ============================================================================
const UserInfo = memo(function UserInfo(props: { userObj: UserType }) {
  const { userObj } = props;
  return (
    <div className="p-3">
      <p className="font-semibold md-text">{userObj?.name}</p>
      <p className="sm-text">
        {userObj?.city} - {userObj?.pincode}
      </p>
      <p className="sm-text w-max ">{userObj?.country}</p>
    </div>
  );
});

const TrackingInfo = memo(function TrackingInfo(props: { shipment: ShipmentObj; isAdmin: boolean }) {
  const { shipment, isAdmin } = props;
  const refNo = shipment?.userReferenceNumber;

  return (
    <div className="p-3">
      <p className="font-semibold md-text">{shipment.shipmentNumber}</p>
      <p className="sm-text">
        <span className="font-semibold">Reference:</span> {refNo}
      </p>
      <p className="sm-text">
        <span className="font-semibold">AWB:</span> {shipment?.carrierDetails?.awb || '-'}
      </p>
      <p className="sm-text">
        <span className="font-semibold">Invoice:</span> {shipment?.invoiceNumber || '-'}
      </p>
      {isAdmin && (
        <Tooltip title={shipment.platform === 'inter organization' ? 'API' : shipment.platform?.toUpperCase()} arrow>
          <span className="sm-text">
            {shipment.platform === 'web' && <LaptopSharpIcon fontSize="medium" />}
            {shipment.platform === 'mobile' && <PhoneIphoneSharpIcon fontSize="medium" />}
            {shipment.platform === 'inter organization' && <AccountTreeSharpIcon fontSize="medium" />}
          </span>
        </Tooltip>
      )}
    </div>
  );
});

const ShipmentActions = memo(function ShipmentActions(props: { shipment: ShipmentObj }) {
  const { shipment } = props;
  const navigate = useNavigate();
  const isAdmin = !userLogin();
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);

  const shipmentNumber = shipment?.shipmentNumber || '';

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isRepeatOrderAllowed = useMemo(
    () =>
      isActionAllowed(
        accessGroupById?.actions,
        ACTION_NAMES.Single_Shipment_View_OR_Processing.repeat_shipment,
        PERFORM_ACTION.write,
      ),
    [accessGroupById?.actions],
  );

  const carrierImage = useMemo(() => getCarrierImage(shipment, isAdmin), [shipment, isAdmin]);

  const handleViewClick = useCallback(() => {
    setDocumentUploadModalOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDocumentUploadModalOpen(false);
  }, []);

  const handleRepeatOrderClick = useCallback(() => {
    if (isRepeatOrderAllowed) {
      navigate(
        `/shipment/create-single?ref=${shipment.shipmentNumber}${isAdmin ? `&accountId=${shipment._userId._id}` : ''}`,
      );
    }
  }, [isRepeatOrderAllowed, navigate, shipment.shipmentNumber, shipment._userId._id, isAdmin]);

  const isRepeatDisabled = useMemo(
    () => (isAdmin && !shipment?._accountId?.isAccountHolder ? true : !isRepeatOrderAllowed),
    [isAdmin, shipment?._accountId?.isAccountHolder, isRepeatOrderAllowed],
  );

  return (
    <>
      {documentUploadModalOpen && (
        <ShipmentKycUpload
          shipmentNumber={shipmentNumber}
          open={documentUploadModalOpen}
          handleClose={handleClose}
          dragDropWrapperOnClickRegister
        />
      )}

      <div className="p-2 flex flex-col items-center justify-center">
        <DownloadShipmentLabelAndInvoice
          handleView={handleViewClick}
          carrierAWBIcon={carrierImage}
          hideCarrierAWB={hideDownloadCarrierAWBAction(shipment)}
          shipmentNumber={shipmentNumber}
          shipmentId={shipment._id}
        />

        <Grid container item justifyContent="center" className="mt-2">
          <SbButton
            containerClass="py-1 px-3 normal-case xs-text"
            sx={{ height: '26px', borderRadius: '12px' }}
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={handleRepeatOrderClick}
            disabled={isRepeatDisabled}
          >
            Repeat Order
          </SbButton>
        </Grid>
      </div>
    </>
  );
});

// ============================================================================
// SELECTOR FUNCTIONS - Optimized with shallowEqual
// ============================================================================

function ShipmentHistoryTable(props: { isAdmin: boolean }) {
  const {
    data: { list, currentPage, perPage },
    selectedShipments,
  } = useSelector((state: RootStateType) => state.shipmentHistory);
  const serviceList = useSelector((state: RootStateType) => state?.carrierAndService?.serviceList);
  const navigate = useNavigate();
  const isAdmin = hasAdminAccess();
  const isUser = userLogin();
  const dispatch = useDispatch<AppDispatch>();
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const onSelectShipment = useCallback(
    (shipmentId: string, isChecked: boolean, status: any, statusHistory: string[]) => {
      const newSelectedShipment = { ...selectedShipments };
      if (isChecked) {
        newSelectedShipment[shipmentId] = {
          isSelected: true,
          status: status,
          isInTransit: hasIsTransit(statusHistory ? statusHistory : [status]),
        };
      } else {
        delete newSelectedShipment[shipmentId];
      }
      dispatch(setSelectedShips(newSelectedShipment));
    },
    [dispatch, selectedShipments],
  );

  interface FirstResponseItem {
    selectedCourierId: string;
    [key: string]: any;
  }

  interface CarrierId {
    _id: string;
    name: string;
    lowerName: string;
    logo: string;
    isAccountHolder: boolean;
    code: string;
    isActive: boolean;
    __v: number;
  }

  interface Service {
    legs: string[];
  }

  interface DimWtDivisor {
    pound: number;
    kg: number;
  }

  interface SecondResponseItem {
    service: Service;
    dimWtDivisor: DimWtDivisor;
    _id: string;
    carrierId: CarrierId;
    name: string;
    logo: string;
    isApiIntegrated: boolean;
    integrationDetails: any[];
    boxWiseRates: boolean;
    isActive: boolean;
    __v: number;
  }

  const getCourierName = useCallback(
    (firstResponse: FirstResponseItem[], secondResponse: SecondResponseItem[]): (string | null)[] => {
      return firstResponse.map(item => {
        const courierId = item.selectedCourierId;
        const matchingService = secondResponse.find(service => service._id === courierId);
        return matchingService ? matchingService.name : null;
      });
    },
    [],
  );

  const courierNames = useMemo(
    () => getCourierName(list as any, serviceList as any),
    [list, serviceList, getCourierName],
  );

  const checkAllShipments = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newShipmentCheckedList = list.reduce(
          (
            acc: { [k: string]: { isSelected: boolean; status: string; isInTransit: boolean } },
            shipment: ShipmentObj,
          ) => {
            acc[shipment.shipmentNumber] = {
              isSelected: true,
              status: shipment.status as string,
              isInTransit: hasIsTransit(shipment.statusHistory),
            };
            return acc;
          },
          {},
        );
        dispatch(setSelectedShips(newShipmentCheckedList));
      } else {
        dispatch(setSelectedShips({}));
      }
    },
    [dispatch, list],
  );

  return (
    <SBTableWrapper>
      <SBTable className="rounded-md border-none" stickyHeader>
        <TableHead>
          <TableRow>
            <TableHeadCell>
              <Checkbox
                style={{ color: 'inherit' }}
                checked={Object.keys(selectedShipments).length === list.length}
                onChange={checkAllShipments}
              />
            </TableHeadCell>
            <TableHeadCell style={{ minWidth: '80px' }}>Sr no.</TableHeadCell>
            <TableHeadCell>Date</TableHeadCell>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell style={{ minWidth: '250px' }}>Shipper</TableHeadCell>
            <TableHeadCell style={{ minWidth: '250px' }}>Consignee</TableHeadCell>
            {props.isAdmin && <TableHeadCell style={{ minWidth: '220px' }}>Service Name</TableHeadCell>}
            <TableHeadCell>Tracking</TableHeadCell>
            <TableHeadCell style={{ minWidth: '120px' }}>Status</TableHeadCell>
            <TableHeadCell>Char.Weight</TableHeadCell>
            {props.isAdmin && name !== 'Cargo Solutions Network' && (
              <TableHeadCell style={{ minWidth: '120px' }}>Type</TableHeadCell>
            )}
            <TableHeadCell>Actions</TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((shipment, index) => {
            const routeTypeObj = SHIPMENT_ROUTE_TYPE[shipment.shipmentRouteType] || {};
            const statusObj = SHIPMENT_STATUS[shipment.status] || {};
            const isUnprocessed = shipment.status === SHIPMENT_STATUS?.unprocessed?.value;
            return (
              <SBTableRow
                key={`${shipment.shipmentNumber}-${index}`}
                className={selectedShipments[shipment.shipmentNumber] ? 'selected cursor-pointer' : 'cursor-pointer'}
                onClick={(e: React.MouseEvent<HTMLTableRowElement>) => {
                  const { target } = e;
                  if ((target as HTMLInputElement).getAttribute('type') === 'checkbox') {
                    return;
                  }
                  return navigate(
                    `${
                      isAdmin || !isUser
                        ? `/shipment/admin${isUnprocessed ? '/unprocessed' : ''}/edit`
                        : '/shipment/history'
                    }/${shipment.shipmentNumber}`,
                  );
                }}
              >
                <TableRowCell
                  align="center"
                  onClick={event => {
                    // event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <div>
                    <Checkbox
                      style={{ color: 'inherit' }}
                      onChange={e => {
                        onSelectShipment(
                          shipment.shipmentNumber,
                          e.target.checked,
                          shipment.status,
                          shipment.statusHistory,
                        );
                      }}
                      checked={!!selectedShipments[shipment.shipmentNumber]}
                    />
                  </div>
                </TableRowCell>
                <TableRowCell align="center" className="md-text">
                  <div>{index + 1 + (currentPage - 1) * perPage}</div>
                </TableRowCell>
                <TableRowCell align="center" className="md-text">
                  {moment(shipment.createdAt).format('DD/MM/YYYY')}
                </TableRowCell>
                <TableRowCell align="center" className="md-text">
                  {/* {moment(shipment.createdAt).format('DD/MM/YYYY')} */}
                  {shipment?.company?.name ? shipment?.company?.name : shipment?._userId?.name || 'N/A'}
                </TableRowCell>
                <TableRowCell>
                  <UserInfo userObj={shipment._shipperId} />
                </TableRowCell>
                <TableRowCell>
                  <UserInfo userObj={shipment._consigneeId} />
                </TableRowCell>
                {props.isAdmin && (
                  <TableRowCell align="center" className="sm-text">
                    {courierNames[index] ? courierNames[index] : shipment?.sellingPricing?.serviceName}
                  </TableRowCell>
                )}
                <TableRowCell
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <TrackingInfo shipment={shipment} isAdmin={props.isAdmin} />
                </TableRowCell>
                <TableRowCell align="center">
                  <Typography
                    className="p-1 rounded-xs sm-text font-medium capitalize w-max flex justify-self-center"
                    sx={{
                      color: statusObj.textColor,
                      background: statusObj.bgColor,
                      px: 1,
                      py: 0.5,
                      justifySelf: 'center',
                    }}
                  >
                    {statusObj.label}
                  </Typography>
                </TableRowCell>
                <TableRowCell className="md-text font-semibold" align="center">
                  {shipment.sellingPricing?.displayChargeableWeight != null
                    ? `${shipment.sellingPricing.displayChargeableWeight} kg`
                    : shipment.sellingPricing?.displayChargeableWeightInLbs != null
                    ? `${shipment.sellingPricing.displayChargeableWeightInLbs} ${
                        shipment.sellingPricing.displayUnitInLbs || 'lb'
                      }`
                    : '-'}
                </TableRowCell>
                {props.isAdmin && name !== 'Cargo Solutions Network' && (
                  <TableRowCell align="center">
                    <Typography
                      className="p-1 rounded-xs capitalize"
                      sx={{ color: routeTypeObj.textColor, background: routeTypeObj.bgColor, px: 1, py: 0.5 }}
                    >
                      {routeTypeObj.label || shipment.status}
                    </Typography>
                  </TableRowCell>
                )}
                <TableRowCell
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <ShipmentActions shipment={shipment} />
                </TableRowCell>
              </SBTableRow>
            );
          })}
        </TableBody>
      </SBTable>
    </SBTableWrapper>
  );
}

const ImportExportActions = memo(function ImportExportActions(props: {
  isAdmin: boolean;
  getFilterObj: (options: FilterObj) => ShipmentHistoryQuery;
  onOpenAISearch: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [bulkAnchor, setBulkAnchor] = useState<null | HTMLElement>(null);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState<boolean>(false);
  const [showBulkAWBModal, setShowBulkAWBModal] = useState<boolean>(false);

  const { limit } = useSelector((state: RootStateType) => state.notifications);
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isBulkStatusUploadAllowed = useMemo(
    () =>
      isActionAllowed(accessGroupById?.actions, ACTION_NAMES.Shipment_Details.bulk_status_upload, PERFORM_ACTION.write),
    [accessGroupById?.actions],
  );

  const closeMenu = useCallback(() => {
    setAnchor(null);
  }, []);

  const closeBulkMenu = useCallback(() => {
    setBulkAnchor(null);
  }, []);

  const refreshNotifications = () => {
    dispatch(getAllUnreadNotifications({ page: 1, limit }));
    dispatch(getAllReadNotifications({ page: 1, limit }));
  };

  const openSuccessModal = ({ title, subTitle }: { title: string; subTitle: string }) => {
    dispatch(
      SuccessErrorModalOpen({
        type: 'success',
        title,
        subTitle,
        btnText: 'Close',
        onConfirm: () => {
          dispatch(toggleBoolean());
          dispatch(SuccessErrorModalReset());
        },
      }),
    );
  };

  const openErrorModal = (error: any) => {
    dispatch(
      SuccessErrorModalOpen({
        type: 'error',
        title: error?.response?.data?.msg || 'Something went wrong',
        subTitle: error?.response?.data?.errorDetails || 'Please try after sometimes',
      }),
    );
  };

  const onMenuItemClick = useCallback(
    async (page: string) => {
      setLoading(true);

      try {
        const filter = props.getFilterObj({});

        // -------------------------
        // SHIPMENT INVOICE REPORT
        // -------------------------
        if (page === 'SHIPMENT_INVOICE_REPORT') {
          dispatch(getShipmentInvoiceReport(filter));

          refreshNotifications();

          openSuccessModal({
            title: 'Download in Progress',
            subTitle:
              'Your shipment invoice report is being generated. You will receive a notification once it is ready for download.',
          });

          return;
        }

        // -------------------------
        // OTHER DOWNLOADS
        // -------------------------
        const response = await API.get('/shipment/download-list', {
          params: {
            downloadType: page,
            ...filter,
          },
        });

        if (response?.data) {
          dispatch(fetchShipmentReports());
          refreshNotifications();

          openSuccessModal({
            title: 'Download in Progress',
            subTitle: response.data.msg,
          });
        }
      } catch (error) {
        openErrorModal(error);
      } finally {
        setLoading(false);
        setAnchor(null);
      }
    },
    [dispatch, limit, props],
  );

  const handleUserShipmentsExport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get('/shipment/download-list', {
        params: {
          downloadType: 'USER_SUMMARY',
          ...props.getFilterObj({}),
        },
      });

      if (response?.data) {
        dispatch(fetchShipmentReports());
        dispatch(getAllUnreadNotifications({ page: 1, limit }));
        dispatch(getAllReadNotifications({ page: 1, limit }));
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Download in Progress',
            subTitle: response?.data?.msg,
            btnText: 'Close',
            onConfirm: () => {
              dispatch(toggleBoolean());
              dispatch(SuccessErrorModalReset());
            },
          }),
        );
      }
    } catch (error) {
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Something went wrong',
          subTitle: 'please try after sometimes',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, limit, props]);

  const openMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor(event.currentTarget);
  }, []);

  const openBulkMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setBulkAnchor(event.currentTarget);
  }, []);

  const onMenuItemBulkClick = useCallback(
    async (type: string) => {
      if (type === 'status' && isBulkStatusUploadAllowed) {
        setShowBulkStatusModal(true);
      } else {
        setShowBulkAWBModal(true);
      }
    },
    [isBulkStatusUploadAllowed],
  );

  const reportPermissions = useMemo(
    () =>
      (Object.keys(ACTION_NAMES.Shipment_Reports) as (keyof typeof ACTION_NAMES.Shipment_Reports)[]).reduce(
        (acc, key) => {
          const formattedKey =
            'is' +
            key
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join('') +
            'Allowed';

          acc[formattedKey] =
            isActionAllowed(accessGroupById?.actions, ACTION_NAMES.Shipment_Reports[key], PERFORM_ACTION.write) ??
            false;

          return acc;
        },
        {} as Record<string, boolean>,
      ),
    [accessGroupById?.actions],
  );

  const options = [
    { value: 'ACCOUNTS', label: 'Account Detailed' },
    { value: 'ACCOUNTS_SUMMARY', label: 'Accounts Summary' },
    { value: 'CS', label: 'Customer Service Detailed' },
    { value: 'OP', label: 'Operation Detailed' },
    { value: 'SALES', label: 'Sales Detailed' },
    { value: 'ALL', label: 'Shipment Report Detailed' },
    { value: 'CS_SUMMARY', label: 'Customer Service Summary' },
    { value: 'OP_SUMMARY', label: 'Operation Summary' },
    { value: 'SALES_SUMMARY', label: 'Sales Summary' },
    { value: 'ALL_SUMMARY', label: 'Shipment Report Summary' },
    { value: 'SHIPMENT_INVOICE_REPORT', label: 'Shipment Invoice Report' },
  ];

  const valueMap = {
    accounts_report_detailed: 'ACCOUNTS',
    accounts_report_summary: 'ACCOUNTS_SUMMARY',
    customer_service_report_detailed: 'CS',
    customer_service_report_summary: 'CS_SUMMARY',
    operation_report_detailed: 'OP',
    operation_report_summary: 'OP_SUMMARY',
    sales_report_detailed: 'SALES',
    sales_report_summary: 'SALES_SUMMARY',
    shipment_report_detailed: 'ALL',
    shipment_report_summary: 'ALL_SUMMARY',
    shipment_invoice_report: 'SHIPMENT_INVOICE_REPORT',
  };

  const reportOptions = useMemo(
    () =>
      (Object.keys(ACTION_NAMES.Shipment_Reports) as (keyof typeof ACTION_NAMES.Shipment_Reports)[])
        .map(key => {
          const formattedKey =
            'is' +
            key
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join('') +
            'Allowed';

          return {
            key,
            label:
              options.find(opt => opt.value === valueMap[key])?.label ||
              key
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '),

            value: valueMap[key],
            isAllowed: reportPermissions[formattedKey],
          };
        })
        .filter(option => option.isAllowed),
    [reportPermissions],
  );

  return (
    <>
      <div className="overflow-auto" id="fadedScroll">
        <Grid
          container
          justifyContent={{ xs: props.isAdmin ? '' : 'flex-end', md: 'flex-end' }}
          flexWrap={{ xs: 'nowrap', md: 'wrap' }}
          style={{ marginTop: '52px', marginBottom: '25px' }}
        >
          {props.isAdmin ? (
            <>
              <SbButton
                startIcon={<AutoAwesomeIcon sx={{ color: '#7c3aed' }} />}
                className="border-1px border-light mr-2"
                sx={{
                  borderRadius: '8px !important',
                  borderColor: 'rgba(124, 58, 237, 0.3)',
                  backgroundColor: '#faf5ff',
                  color: '#7c3aed',
                  fontWeight: 600,
                  height: '40px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#f5f3ff',
                    borderColor: '#7c3aed',
                  },
                }}
                onClick={props.onOpenAISearch}
                variant="outlined"
              >
                AI Smart Search
              </SbButton>

              <SbButton
                startIcon={<Image src={'/images/file_upload.svg'} alt="upload" />}
                endIcon={<ArrowDropDownCircleOutlined />}
                className="border-1px border-light mr-2"
                sx={{ borderRadius: '8px !important', minWidth: '225px' }}
                onClick={openBulkMenu}
                variant="outlined"
                disabled={!isBulkStatusUploadAllowed}
              >
                Bulk Status Upload
              </SbButton>

              <Menu
                open={Boolean(bulkAnchor)}
                anchorEl={bulkAnchor}
                onClose={closeBulkMenu}
                keepMounted
                PaperProps={{ sx: { width: '250px' } }}
              >
                <Loader loading={loading} />
                {bulkUploadOptions.map(option => (
                  <MenuItem onClick={_ => onMenuItemBulkClick(option.value)} key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>

              <SbButton
                variant="contained"
                sx={{ borderRadius: '8px !important', padding: '8px !important', width: '120px' }}
                onClick={openMenu}
                endIcon={<ArrowDropDownCircleOutlined />}
                disabled={reportOptions.length === 0}
              >
                Export
              </SbButton>

              <Menu
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={closeMenu}
                keepMounted
                PaperProps={{ sx: { width: '250px' } }}
              >
                <Loader loading={loading} />
                {reportOptions.map(option => (
                  <MenuItem onClick={_ => onMenuItemClick(option.value)} key={option.key} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <>
              <SbButton
                startIcon={<AutoAwesomeIcon sx={{ color: '#7c3aed' }} />}
                className="border-1px border-light mr-2"
                sx={{
                  borderRadius: '8px !important',
                  borderColor: 'rgba(124, 58, 237, 0.3)',
                  backgroundColor: '#faf5ff',
                  color: '#7c3aed',
                  fontWeight: 600,
                  height: '40px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#f5f3ff',
                    borderColor: '#7c3aed',
                  },
                }}
                onClick={props.onOpenAISearch}
                variant="outlined"
              >
                AI Smart Search
              </SbButton>
              <ExportBtn
                iconSrc="/images/upload_document.svg"
                alt="Export orders"
                label="Export Orders"
                onClick={handleUserShipmentsExport}
              />
            </>
          )}
        </Grid>
      </div>
      <BulkStatusUpload open={showBulkStatusModal} handleClose={() => setShowBulkStatusModal(false)} />
      <BulkAWBUpload open={showBulkAWBModal} handleClose={() => setShowBulkAWBModal(false)} />
    </>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function ShipmentHistory() {
  const detailServiceList = useSelector((state: RootStateType) => state?.carrierAndService?.serviceList);
  const [isAiFullScreen, setIsAiFullScreen] = useState(false);
  const carrierList = useSelector((state: RootStateType) => state?.carrier?.activeCarrierDropdownList);
  const [filterSelectValue, setFilterSelectValue] = useState('shipmentNumber');
  const [filterText, setFilterText] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  const [system, setSystem] = useState<string[]>([]);
  const [type, setType] = useState<string[]>([]);
  const [platform, setPlatform] = useState<string[]>([]);
  const [invoiced, setInvoiced] = useState<string[]>([]);
  const [carrier, setCarrier] = useState<string[]>([]);
  const [service, setService] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(1, 'months').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate(),
  });
  const [updateStatus, setUpdateStatus] = useState<{ isOpen: boolean }>({ isOpen: false });
  const [createManifest, setCreatemanifest] = useState<{ isOpen: boolean }>({ isOpen: false });
  const [createManifestSuccessModal, setCreateManifestSuccessModal] = useState<{ isOpen: boolean }>({ isOpen: false });
  const [fileDownloadOption, setFileDownloadOption] = useState<FileDownloadOptionPropsType>(fileDownloadDefaultOption);

  const dispatch = useDispatch<AppDispatch>();

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isBulkStatusUploadAllowed = useMemo(
    () => isActionAllowed(accessGroupById?.actions, ACTION_NAMES.Shipment_Details.change_status, PERFORM_ACTION.write),
    [accessGroupById?.actions],
  );

  const isDownloadCarrierAWBAllowed = useMemo(
    () =>
      isActionAllowed(
        accessGroupById?.actions,
        ACTION_NAMES.Shipment_Details.download_carrier_awb,
        PERFORM_ACTION.write,
      ),
    [accessGroupById?.actions],
  );

  const isDownloadShipmentlabelAllowed = useMemo(
    () =>
      isActionAllowed(
        accessGroupById?.actions,
        ACTION_NAMES.Shipment_Details.download_shipment_label,
        PERFORM_ACTION.write,
      ),
    [accessGroupById?.actions],
  );

  const isDownloadProformaInvoiceAllowed = useMemo(
    () =>
      isActionAllowed(
        accessGroupById?.actions,
        ACTION_NAMES.Shipment_Details.download_proforma_invoice,
        PERFORM_ACTION.write,
      ),
    [accessGroupById?.actions],
  );

  const isCreateManifestAllowed = useMemo(
    () => isActionAllowed(accessGroupById?.actions, ACTION_NAMES.Manifest.create_manifest, PERFORM_ACTION.write),
    [accessGroupById?.actions],
  );

  const { success: manifestSuccess } = useSelector((state: RootStateType) => state.manifest);
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  const { success: statusUpdated, bulkStatusUploaded } = useSelector((state: RootStateType) => state.shipmentHistory);
  const loading = useSelector((state: RootStateType) => state.System.loading || state.shipmentHistory.loading);
  const {
    data: { currentPage, perPage, totalCount, list },
    selectedShipments,
  } = useSelector((state: RootStateType) => state.shipmentHistory);

  const isAdmin = !userLogin();
  const isUser = userLogin();

  useEffect(() => {
    const fromDate = moment(dateRange.startDate).format('DD/MM/YYYY');
    const toDate = moment(dateRange.endDate).format('DD/MM/YYYY');
    dispatch(getShipmentList({ currentPage: 1, perPage: 10, fromDate, toDate }));
    dispatch(getLoggedInUserSystemList());
    dispatch(getCarrierList({}));
  }, []);

  const getFilterObj = useCallback(
    (options: FilterObj): ShipmentHistoryQuery => {
      const filterDate = options.dateRange || dateRange;
      const fromDate = moment(filterDate.startDate).format('DD/MM/YYYY');
      const toDate = moment(filterDate.endDate).format('DD/MM/YYYY');
      const filterStatus = options.status || status;
      const filterSystem = options.systems || system;
      const filterType = options.type || type;
      const filterPlatform = options.platform || platform;
      const filterInvoiced = options.invoiced || invoiced;
      const filterCarrier = options.carrier || carrier;
      const filterService = options.service || service;
      const filterSearchKey = filterSelectValue;
      const filterSearchValue = 'searchValue' in options ? options.searchValue : filterText;
      const filterQuery: ShipmentHistoryQuery = {
        currentPage: options.currentPage || currentPage,
        perPage: options.perPage || perPage,
        fromDate,
        toDate,
        status: filterStatus,
        searchKey: filterSearchKey,
        searchValue: filterSearchValue,
        invoiced: filterInvoiced,
        carrier: filterCarrier,
        service: filterService,
      };
      if (isAdmin) {
        filterQuery['systems'] = filterSystem;
        filterQuery['type'] = filterType;
        filterQuery['platform'] = filterPlatform;
      }
      if (!filterSearchValue) {
        delete filterQuery['searchKey'];
        delete filterQuery['searchValue'];
      }
      return filterQuery;
    },
    [
      dateRange,
      status,
      system,
      type,
      platform,
      invoiced,
      carrier,
      service,
      filterSelectValue,
      filterText,
      currentPage,
      perPage,
      isAdmin,
    ],
  );

  const handleAiResults = useCallback(
    (filters: AISearchResult['filters'] | null, activeResult: AISearchResult | null) => {
      if (!activeResult) {
        dispatch(getShipmentList(getFilterObj({})));
        return;
      }

      if (filters) {
        const finalFromDate = filters.fromDate
          ? moment(filters.fromDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
          : moment(dateRange.startDate).format('DD/MM/YYYY');
        const finalToDate = filters.toDate
          ? moment(filters.toDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
          : moment(dateRange.endDate).format('DD/MM/YYYY');

        const merged: ShipmentHistoryQuery = {
          currentPage: 1,
          perPage,
          fromDate: finalFromDate,
          toDate: finalToDate,
          status: filters.status ? [filters.status] : status,
          carrier: filters.carrier ? [filters.carrier] : carrier,
          invoiced: filters.invoiceStatus ? [filters.invoiceStatus] : invoiced,
          service,
        };

        if (isAdmin) {
          merged['systems'] = system;
          merged['type'] = type;
          merged['platform'] = platform;
        }

        if (filters.routeType) {
          const rt = String(filters.routeType).toLowerCase();
          if (rt === 'import') merged.import = true;
          else if (rt === 'export') merged.export = true;
          else if (rt === 'domestic') merged.domestic = true;
        }

        dispatch(getShipmentList(merged));
      }
    },
    [dispatch, getFilterObj, dateRange, status, carrier, invoiced, service, perPage, isAdmin, system, type, platform],
  );

  const onDateChange = useCallback(
    ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
      if (isOpen) return;
      const dateRange = { startDate, endDate };
      setDateRange(dateRange);
      dispatch(getShipmentList(getFilterObj({ dateRange, currentPage: 1, perPage: 10 })));
    },
    [dispatch, getFilterObj],
  );

  const onStatusFilter = useCallback(
    (selected: Array<string>) => {
      setStatus(selected);
      dispatch(getShipmentList(getFilterObj({ status: selected, currentPage: 1, perPage: 10 })));
    },
    [dispatch, getFilterObj],
  );

  const onSystemFilter = useCallback(
    (selected: Array<string>) => {
      setSystem(selected);
      dispatch(getShipmentList(getFilterObj({ systems: selected, currentPage: 1, perPage: 10 })));
    },
    [dispatch, getFilterObj],
  );

  const onTypeFilter = useCallback(
    (selected: Array<string>) => {
      setType(selected);
      dispatch(getShipmentList(getFilterObj({ type: selected, currentPage: 1, perPage: 10 })));
    },
    [dispatch, getFilterObj],
  );

  const onPlatformFilter = useCallback(
    (selected: Array<string>) => {
      setPlatform(selected);
      dispatch(getShipmentList(getFilterObj({ platform: selected, currentPage: 1, perPage: 10 })));
    },
    [dispatch, getFilterObj],
  );

  const onInvoicedFilter = useCallback(
    (selected: Array<string>) => {
      setInvoiced(selected);
      dispatch(getShipmentList(getFilterObj({ invoiced: selected, currentPage: 1, perPage: 10 })));
    },
    [dispatch, getFilterObj],
  );

  const onCarrierFilter = useCallback(
    (selected: Array<string>) => {
      setCarrier(selected);
      dispatch(getShipmentList(getFilterObj({ carrier: selected, currentPage: 1, perPage: 10 })));
    },
    [dispatch, getFilterObj],
  );

  const onServiceFilter = useCallback(
    (selected: Array<string>) => {
      setService(selected);
      dispatch(getShipmentList(getFilterObj({ service: selected, currentPage: 1, perPage: 10 })));
    },
    [dispatch, getFilterObj],
  );

  useEffect(() => {
    if (manifestSuccess) {
      setCreatemanifest({ isOpen: false });
      setCreateManifestSuccessModal({ isOpen: true });
      dispatch(resetManifestSuccess());
      dispatch(setSelectedShips({}));
    }
  }, [manifestSuccess, dispatch]);

  const handleManifestSuccessModalClose = useCallback(() => {
    setCreateManifestSuccessModal({ isOpen: false });
    dispatch(resetManifest());
  }, [dispatch]);

  useEffect(() => {
    if (statusUpdated) {
      setUpdateStatus({ isOpen: false });
      dispatch(getShipmentList(getFilterObj({ currentPage: 1, perPage: 10 })));
    }
  }, [statusUpdated, dispatch, getFilterObj]);

  useEffect(() => {
    if (bulkStatusUploaded) {
      setTimeout(() => {
        dispatch(getShipmentList(getFilterObj({ currentPage: 1, perPage: 10 })));
      }, 2000);
    }
  }, [bulkStatusUploaded, dispatch, getFilterObj]);

  const serviceListOptions = useMemo(
    () => getFilteredServiceList(detailServiceList, carrier),
    [detailServiceList, carrier],
  );

  const handleUpdateStatusClose = useCallback(() => {
    setUpdateStatus({ isOpen: false });
    setTimeout(() => {
      dispatch(getShipmentList(getFilterObj({})));
    }, 1000);
  }, [dispatch, getFilterObj]);

  const handleCreateManifestClose = useCallback(() => {
    setCreatemanifest({ isOpen: false });
  }, []);

  const handleFileDownloadClose = useCallback(() => {
    setFileDownloadOption(fileDownloadDefaultOption);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
    setService([]);
    setCarrier([]);
    setInvoiced([]);
    setSystem([]);
    setType([]);
    setPlatform([]);
    setFilterText('');
    setDateRange({
      startDate: moment().subtract(1, 'months').startOf('day').toDate(),
      endDate: moment().endOf('day').toDate(),
    });
    dispatch(
      getShipmentList(
        getFilterObj({
          status: [],
          platform: [],
          service: [],
          carrier: [],
          invoiced: [],
          type: [],
          searchValue: '',
          dateRange: {
            startDate: moment().subtract(1, 'months').startOf('day').toDate(),
            endDate: moment().endOf('day').toDate(),
          },
          currentPage: 1,
          perPage: 10,
        }),
      ),
    );
  }, [dispatch, getFilterObj]);

  const handleTextSearch = useCallback(() => {
    dispatch(getShipmentList(getFilterObj({ currentPage: 1, perPage: 10 })));
  }, [dispatch, getFilterObj]);

  const handleTextClear = useCallback(() => {
    setFilterText('');
    dispatch(getShipmentList(getFilterObj({ searchValue: undefined, currentPage: 1, perPage: 10 })));
  }, [dispatch, getFilterObj]);

  const handleSelectChange = useCallback((value: string) => {
    setFilterSelectValue(value);
  }, []);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  }, []);

  const handleClearSelection = useCallback(() => {
    dispatch(setSelectedShips({}));
  }, [dispatch]);

  const handleChangeStatusClick = useCallback(() => {
    if (isBulkStatusUploadAllowed) {
      setUpdateStatus({ isOpen: true });
    }
  }, [isBulkStatusUploadAllowed]);

  const handleCarrierLabelClick = useCallback(() => {
    Object.keys(selectedShipments).forEach((item, index) => {
      setTimeout(() => {
        const shipmentDetails = list?.find(obj => obj?.shipmentNumber === item);
        if ((!isUser || shipmentDetails?.carrierDetails?.awbShowToUser) && shipmentDetails?.carrierDetails?.awb) {
          dispatch(downloadCarrierLabel(item));
        }
      }, index * 3000);
    });
  }, [selectedShipments, list, isUser, dispatch]);

  const handleShippingLabelClick = useCallback(() => {
    if (isDownloadShipmentlabelAllowed) {
      setFileDownloadOption({
        isOpen: true,
        downloadOption: 'fileDimention',
        title: 'Download Shipping Label',
        handleDownload: fileType =>
          dispatch(downloadBulkShippingLabel({ fileType, shipmentNumber: Object.keys(selectedShipments) })),
      });
    }
  }, [isDownloadShipmentlabelAllowed, selectedShipments, dispatch]);

  const handleShippingInvoiceClick = useCallback(() => {
    setFileDownloadOption({
      isOpen: true,
      downloadOption: 'fileFormat',
      title: 'Download Invoice',
      handleDownload: fileType =>
        dispatch(downloadBulkShippingInvoice({ fileType, shipmentNumber: Object.keys(selectedShipments) })),
    });
  }, [selectedShipments, dispatch]);

  const handleProformaInvoiceClick = useCallback(() => {
    if (isDownloadProformaInvoiceAllowed) {
      dispatch(downloadBulkProformaInvoice({ shipmentNumbers: Object.keys(selectedShipments) }));
    }
  }, [isDownloadProformaInvoiceAllowed, selectedShipments, dispatch]);

  const handleCreateManifestClick = useCallback(() => {
    if (isCreateManifestAllowed) {
      setCreatemanifest({ isOpen: true });
    }
  }, [isCreateManifestAllowed]);

  const handlePageChange = useCallback(
    (_: any, page: number) => {
      dispatch(getShipmentList(getFilterObj({ currentPage: page + 1 })));
    },
    [dispatch, getFilterObj],
  );

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(getShipmentList(getFilterObj({ perPage: parseInt(event.target.value) })));
    },
    [dispatch, getFilterObj],
  );

  const selectedShipmentsCount = Object.keys(selectedShipments).length;

  return (
    // <Loader loading={loading} overly>
    <>
      <DownloadOptions {...fileDownloadOption} handleClose={handleFileDownloadClose} />
      <UpdateStatus
        isOpen={updateStatus.isOpen}
        handleClose={handleUpdateStatusClose}
        selectedShipments={Object.keys(selectedShipments)}
      />
      <CreateManifest
        isOpen={createManifest.isOpen}
        handleClose={handleCreateManifestClose}
        selectedShipments={Object.keys(selectedShipments)}
      />
      <CreateManifestSuccessModal
        isOpen={createManifestSuccessModal.isOpen}
        handleClose={handleManifestSuccessModalClose}
      />

      <Title title={isAdmin ? 'Shipment Details' : 'Shipment History'} removeBorder />
      <Navigation />
      <ImportExportActions
        getFilterObj={getFilterObj}
        isAdmin={Boolean(isAdmin)}
        onOpenAISearch={() => setIsAiFullScreen(prev => !prev)}
      />

      <AISmartSearch isOpen={isAiFullScreen} onClose={() => setIsAiFullScreen(false)} onResults={handleAiResults} />

      {!isAiFullScreen && selectedShipmentsCount === 0 && (
        <div className="overflow-auto" id="fadedScroll">
          <TableActionContainer isAdmin={isAdmin ? '1' : '0'}>
            <FilterSearch
              onSelectChange={handleSelectChange}
              list={isAdmin ? AdminSearchFilterList : SearchFilterList}
              selectValue={filterSelectValue}
              textValue={filterText}
              onTextChange={handleTextChange}
              onTextSearch={handleTextSearch}
              onTextClear={handleTextClear}
            />
            <div></div>
            <DateRangePicker onChange={onDateChange} value={dateRange} />
            <FilterSelect id="status" label="Status" options={StatusList} value={status} onChange={onStatusFilter} />
            <FilterSelect
              id="invoiced"
              label="Invoice"
              options={InvoicedList}
              value={invoiced}
              onChange={onInvoicedFilter}
            />
            {isAdmin && (
              <>
                <FilterSelect
                  id="carrier"
                  label="Carrier"
                  options={carrierList}
                  value={carrier}
                  onChange={onCarrierFilter}
                />
                <FilterSelect
                  id="service"
                  label="Service"
                  options={serviceListOptions}
                  value={service}
                  onChange={onServiceFilter}
                />
                <FilterSelect
                  id="system"
                  label="System"
                  options={loggedInUserSystemDropdownList}
                  value={system}
                  onChange={onSystemFilter}
                />
                <FilterSelect id="type" label="Type" options={TypeList} value={type} onChange={onTypeFilter} />
                <FilterSelect
                  id="platform"
                  label="Platform"
                  options={PlatformList}
                  value={platform}
                  onChange={onPlatformFilter}
                />
              </>
            )}
            <SbButton
              className="xs-text ml-1"
              variant="outlined"
              sx={{ borderRadius: '8px !important', padding: '8px !important' }}
              onClick={handleClearFilters}
            >
              Clear Filters
            </SbButton>
          </TableActionContainer>
        </div>
      )}
      {selectedShipmentsCount > 0 && (
        <div className="overflow-auto" id="fadedScroll">
          <TableActionContainer
            style={{ zIndex: 1, gridTemplateColumns: 'repeat(7, min-content)', columnGap: '15px', rowGap: '15px' }}
          >
            <SbButton sx={ButtonSx} endIcon={<ClearIcon />} onClick={handleClearSelection}>
              Selected ({selectedShipmentsCount})
            </SbButton>
            {isUser ? (
              <></>
            ) : (
              <SbButton
                variant="outlined"
                startIcon={<ShippingLabelIcon />}
                sx={ButtonSx}
                className="sm-text"
                onClick={handleChangeStatusClick}
                disabled={!isBulkStatusUploadAllowed}
              >
                Change Status
              </SbButton>
            )}
            <SbButton
              variant="outlined"
              startIcon={<CspLabelIcon />}
              endIcon={<FileDownloadIcon />}
              sx={ButtonSx}
              className="sm-text"
              disabled={!isDownloadCarrierAWBAllowed}
              onClick={handleCarrierLabelClick}
            >
              Carrier Label
            </SbButton>
            <SbButton
              variant="outlined"
              startIcon={<ShippingLabelIcon />}
              endIcon={<FileDownloadIcon />}
              sx={ButtonSx}
              className="sm-text"
              onClick={handleShippingLabelClick}
              disabled={!isDownloadShipmentlabelAllowed}
            >
              Shipping Label
            </SbButton>
            <SbButton
              variant="outlined"
              startIcon={<ShipmentInvoiceIcon />}
              endIcon={<FileDownloadIcon />}
              sx={ButtonSx}
              className="sm-text"
              onClick={handleShippingInvoiceClick}
            >
              Shipping Invoice
            </SbButton>
            <SbButton
              variant="outlined"
              startIcon={<ProformaInvoiceIcon />}
              endIcon={<FileDownloadIcon />}
              sx={ButtonSx}
              className="sm-text"
              onClick={handleProformaInvoiceClick}
              disabled={!isDownloadProformaInvoiceAllowed}
            >
              Proforma Invoice
            </SbButton>
            {isUser ? (
              <></>
            ) : (
              <SbButton
                variant="outlined"
                startIcon={<ProformaInvoiceIcon />}
                sx={ButtonSx}
                className="sm-text"
                onClick={handleCreateManifestClick}
                disabled={!isCreateManifestAllowed}
              >
                Create Manifest
              </SbButton>
            )}
          </TableActionContainer>
        </div>
      )}

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '40vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <ShipmentHistoryTable isAdmin={Boolean(isAdmin)} />
      )}
      {!!totalCount && !!perPage && (
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1}
          rowsPerPage={perPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </>
  );
}
